import cn from "classnames";
import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TwitterPicker } from "react-color";
import {
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputProps,
} from "reactstrap";
import { LabelWembii } from "./LabelWembii";
import "./InputWembii.scss";

interface Props extends InputProps {
  className?: string;
  label?: string;
  name?: string;
  id?: string;
  flat?: boolean;
  disabled?: boolean;
  showShadow?: boolean;
  required?: boolean;
  mb?: boolean;
  rounded?: boolean;
  icon?: ReactNode;
  prepend?: ReactNode;
  invalid?: boolean;
  iconPosition?: "left" | "right";
  style?: object;
  validateTxt?: (arg0: string) => boolean;
  onChange?: (e: any) => void;
  onKeyUp?: (e: any) => void;
  onClick?: (e: any) => void;
}

const normalizeHexColor = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;

  if (/^#[0-9A-Fa-f]{3}$/.test(withHash)) {
    const [, r, g, b] = withHash.match(/^#(.)(.)(.)$/)!;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) {
    return withHash.toLowerCase();
  }

  return null;
};

const toDisplayHex = (value?: string | number | readonly string[]): string => {
  if (value == null || value === "") return "";
  return normalizeHexColor(String(value)) ?? String(value);
};

const toPickerColor = (value?: string | number | readonly string[]): string => {
  return normalizeHexColor(String(value ?? "")) ?? "#0066a2";
};

export const InputWembii = (props: Props) => {
  const {
    className,
    id,
    label,
    min,
    max,
    type,
    flat,
    showShadow,
    rounded,
    required,
    value,
    icon,
    iconPosition = "right",
    disabled = false,
    onChange,
    onKeyUp,
    onClick,
    validateTxt,
    mb = true,
    prepend,
    style = {},
    invalid = false,
    ...attributes
  } = props;
  const [showPicker, setShowPicker] = useState(false);
  const [isHexFocused, setIsHexFocused] = useState(false);
  const [draftHex, setDraftHex] = useState("");
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(
    null
  );
  const swatchRef = useRef<HTMLButtonElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);

  const displayHex = useMemo(() => toDisplayHex(value), [value]);
  const pickerColor = useMemo(() => toPickerColor(value), [value]);

  useEffect(() => {
    if (!isHexFocused) {
      setDraftHex("");
    }
  }, [displayHex, isHexFocused]);

  const emitColor = useCallback(
    (next: string) => {
      const normalized = normalizeHexColor(next);
      if (!normalized) return;
      if (validateTxt && !validateTxt(normalized)) return;
      onChange?.(normalized);
    },
    [onChange, validateTxt]
  );

  const handleChangeColor = useCallback(
    (color: { hex: string }) => {
      setDraftHex(color.hex);
      emitColor(color.hex);
    },
    [emitColor]
  );

  const handleHexInputChange = useCallback(
    (nextValue: string) => {
      setDraftHex(nextValue);
      const normalized = normalizeHexColor(nextValue);
      if (normalized) {
        emitColor(normalized);
      }
    },
    [emitColor]
  );

  const updatePopoverPosition = useCallback(() => {
    const anchor = swatchRef.current ?? groupRef.current;
    if (!anchor) return false;

    const rect = anchor.getBoundingClientRect();
    setPopoverPosition({
      top: rect.bottom + 6,
      left: Math.max(8, rect.left),
    });
    return true;
  }, []);

  const openPicker = useCallback(() => {
    if (disabled) return;
    updatePopoverPosition();
    setShowPicker(true);
  }, [disabled, updatePopoverPosition]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setPopoverPosition(null);
  }, []);

  const togglePicker = useCallback(() => {
    if (disabled) return;
    if (showPicker) {
      closePicker();
      return;
    }
    openPicker();
  }, [disabled, showPicker, closePicker, openPicker]);

  useLayoutEffect(() => {
    if (!showPicker) return;
    updatePopoverPosition();
  }, [showPicker, updatePopoverPosition]);

  useEffect(() => {
    if (!showPicker) return;

    const handleReposition = () => updatePopoverPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [showPicker, updatePopoverPosition]);

  useEffect(() => {
    if (!showPicker) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePicker();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showPicker, closePicker]);

  const handleHexBlur = useCallback(() => {
    const normalized = normalizeHexColor(draftHex || displayHex);
    if (normalized) {
      setDraftHex(normalized);
      emitColor(normalized);
      return;
    }
    setDraftHex(displayHex);
  }, [draftHex, displayHex, emitColor]);

  const handleOnChange = useCallback(
    (nextValue: string) => {
      if (validateTxt) {
        if (validateTxt(nextValue)) onChange?.(nextValue);
      } else {
        onChange?.(nextValue);
      }
    },
    [validateTxt, onChange]
  );

  const hexInputValue = isHexFocused ? draftHex || displayHex : displayHex;

  if (type === "color") {
    return (
      <div ref={groupRef}>
      <FormGroup
        className={cn("d-flex flex-column position-relative input-wembii-color", className, {
          "m-0": !mb,
        })}
      >
        {label && (
          <LabelWembii forid={id} required={required}>
            {label}
          </LabelWembii>
        )}
        <InputGroup className="input-wembii-color__group">
          {prepend && (
            <InputGroupAddon addonType="prepend">{prepend}</InputGroupAddon>
          )}
          <InputGroupAddon addonType="prepend" className="p-0">
            <button
              ref={swatchRef}
              type="button"
              className="input-wembii-color__swatch"
              style={{ "--swatch-color": pickerColor } as React.CSSProperties}
              disabled={disabled}
              aria-label={label || "Color"}
              onClick={(e) => {
                e.stopPropagation();
                togglePicker();
              }}
            />
          </InputGroupAddon>
          <Input
            id={id}
            type="text"
            {...attributes}
            style={{
              borderRadius: rounded ? 15 : 10,
              ...style,
            }}
            disabled={disabled}
            invalid={invalid}
            className={cn("input-wembii-color__hex", className, {
              "border-top-0": flat,
              "border-right-0": flat,
              "border-left-0": flat,
              "rounded-0": flat,
              "shadow-sm": showShadow,
            })}
            autoComplete="off"
            placeholder="#0066a2"
            value={hexInputValue}
            onFocus={() => {
              setIsHexFocused(true);
              setDraftHex(displayHex);
            }}
            onBlur={() => {
              setIsHexFocused(false);
              handleHexBlur();
            }}
            onChange={(e) => handleHexInputChange(e.target.value)}
            onKeyUp={onKeyUp}
          />
        </InputGroup>

        {showPicker &&
          popoverPosition &&
          typeof document !== "undefined" &&
          createPortal(
            <>
              <div
                className="input-wembii-color__backdrop"
                onClick={closePicker}
              />
              <div
                className="input-wembii-color__popover"
                style={{
                  top: popoverPosition.top,
                  left: popoverPosition.left,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <TwitterPicker
                  color={pickerColor}
                  triangle="hide"
                  width="252px"
                  onChange={handleChangeColor}
                  styles={{
                    default: {
                      card: {
                        boxShadow: "none",
                        border: "none",
                        background: "transparent",
                      },
                      body: {
                        padding: "8px 6px",
                      },
                      hash: {
                        display: "none",
                      },
                      input: {
                        display: "none",
                      },
                      clear: {
                        display: "none",
                      },
                    },
                  }}
                />
              </div>
            </>,
            document.body
          )}
      </FormGroup>
      </div>
    );
  }

  return (
    <FormGroup
      className={cn("d-flex flex-column position-relative", className, {
        "m-0": !mb,
      })}
    >
      {label && (
        <LabelWembii forid={id} required={required}>
          {label}
        </LabelWembii>
      )}
      <InputGroup>
        {prepend && (
          <InputGroupAddon addonType="prepend">{prepend}</InputGroupAddon>
        )}
        <Input
          id={id}
          min={min}
          max={max}
          type={type}
          {...attributes}
          style={{
            borderRadius: rounded ? 15 : 10,
            ...style,
          }}
          onKeyUp={onKeyUp}
          disabled={disabled}
          invalid={invalid}
          className={cn(className, {
            "border-top-0": flat,
            "border-right-0": flat,
            "border-left-0": flat,
            "rounded-0": flat,
            "shadow-sm": showShadow,
            "pr-5": icon && iconPosition === "right",
            "pl-5": icon && iconPosition === "left",
          })}
          autoComplete="off"
          value={value}
          onChange={(e) => handleOnChange?.(e.target.value)}
          onClick={onClick}
        />
        {icon && (
          <div
            className={cn(
              "position-absolute p-1 h-100 d-flex align-items-center z-index-5",
              {
                "right-9": iconPosition === "right",
                "left-9": iconPosition === "left",
              }
            )}
          >
            {icon}
          </div>
        )}
      </InputGroup>
    </FormGroup>
  );
};
